





























































































































import {Component, Vue} from "vue-property-decorator";
import Workspaces from "@/state/Workspaces";
import WalletService, {WALLET_TRANSACTION_PAGE_SIZE} from "@/services/WalletService";
import AdminService from "@/services/AdminService";
import CompanyService from "@/services/CompanyService";
import Application from "@/state/Application";
import {processError} from "@/utils/ComponentUtils";
import ShortBankAccount from "@/dto/wallet/ShortBankAccount";
import BankAccountDTO from "@/dto/BankAccountDTO";
import {
    WalletTransactionStatus,
    WalletTransactionType,
    WalletTransactionView,
    WalletView,
} from "@/dto/wallet/WalletDTOs";
import Page, {EMPTY_PAGE} from "@/dto/taxation/Page";
import PaginationComponent from "@/components/new_style_components/PaginationComponent.vue";
import WalletDepositModal from "@/components/profile/company/wallet/WalletDepositModal.vue";
import WalletWithdrawModal from "@/components/profile/company/wallet/WalletWithdrawModal.vue";
import WalletTransferModal from "@/components/profile/company/wallet/WalletTransferModal.vue";
import WalletReverseModal from "@/components/profile/company/wallet/WalletReverseModal.vue";
import SelectOption from "@/components/common/SelectOption";
import moment from "moment";

type AccountBalanceRow = {
  sublimeAccountId: number;
  label: string;
  balanceCents: number;
};

@Component({
  components: {
    PaginationComponent,
    WalletDepositModal,
    WalletWithdrawModal,
    WalletTransferModal,
    WalletReverseModal,
  }
})
export default class CompanyWallet extends Vue {
  WalletTransactionType = WalletTransactionType;
  WalletTransactionStatus = WalletTransactionStatus;

  wallet: WalletView | null = null;
  walletUnavailable = false;
  sublimeAccounts: Array<ShortBankAccount> = [];
  clientAccounts: Array<BankAccountDTO> = [];
  transactionsPage: Page<WalletTransactionView> = EMPTY_PAGE;
  pageNumber = 1;
  relatedCache: Record<number, WalletTransactionView> = {};
  relatedTransaction: WalletTransactionView | null = null;

  get isEmployee(): boolean {
    return Workspaces.isUserSublimeEmployee;
  }

  get companyId(): number {
    return Workspaces.getCurrent.id;
  }

  get transactions(): Array<WalletTransactionView> {
    return this.transactionsPage.content || [];
  }

  get totalPages(): number {
    return this.transactionsPage.totalPages || 0;
  }

  get accountBalances(): Array<AccountBalanceRow> {
    return (this.wallet?.accounts || []).map(account => {
      const sublimeAccount = this.sublimeAccounts.find(item => item.id === account.sublimeAccountId);
      return {
        sublimeAccountId: account.sublimeAccountId,
        label: sublimeAccount
          ? this.formatAccount(sublimeAccount.bankName, account.accountNumber)
          : account.accountNumber || "",
        balanceCents: account.balanceCents,
      };
    });
  }

  get sublimeAccountOptions(): Array<SelectOption> {
    return this.sublimeAccounts.map(account =>
        SelectOption.builder()
            .title(this.formatAccount(account.bankName, account.accountNumber))
            .value(account.id)
            .build(),
    );
  }

  get clientAccountOptions(): Array<SelectOption> {
    return this.clientAccounts.map(account =>
        SelectOption.builder()
            .title(this.formatAccount(account.bankName, account.accountNumber))
            .value(account.id)
            .build(),
    );
  }

  mounted(): void {
    if (!this.isEmployee) {
      return;
    }
    this.load();
  }

  load(): void {
    this.pageNumber = 1;
    this.loadWalletAndCatalogs().then(() => {
      if (this.wallet) {
        this.loadTransactions();
      }
    });
  }

  reloadAfterMutation(): void {
    this.loadWalletAndCatalogs().then(() => {
      if (this.wallet) {
        this.loadTransactions();
      }
    });
  }

  loadWalletAndCatalogs(): Promise<void> {
    Application.startLoading();
    return WalletService.getWallet(this.companyId).then(
        walletRes => {
          this.wallet = walletRes.data;
          this.walletUnavailable = false;
          return Promise.all([
            AdminService.getSublimeAccounts(),
            CompanyService.getBankAccounts(this.companyId),
          ]).then(
              ([sublimeRes, clientRes]) => {
                this.sublimeAccounts = sublimeRes.data || [];
                this.clientAccounts = clientRes.data || [];
                Application.stopLoading();
              },
              err => processError(err, this),
          );
        },
        err => {
          if (err?.response?.status === 404) {
            this.wallet = null;
            this.walletUnavailable = true;
            this.transactionsPage = EMPTY_PAGE;
            Application.stopLoading();
            return;
          }
          processError(err, this);
        },
    );
  }

  loadTransactions(): void {
    Application.startLoading();
    WalletService.getTransactions(this.companyId, this.pageNumber, WALLET_TRANSACTION_PAGE_SIZE).then(
        res => {
          this.transactionsPage = res.data;
          const cache: Record<number, WalletTransactionView> = {};
          (res.data.content || []).forEach(tx => {
            cache[tx.id] = tx;
          });
          this.relatedCache = cache;
          Application.stopLoading();
        },
        err => processError(err, this),
    );
  }

  onChangePage(page: number): void {
    this.pageNumber = page;
    this.loadTransactions();
  }

  openDeposit(): void {
    this.$modal.show("wallet-deposit-modal", {
      companyId: this.companyId,
      clientAccountOptions: this.clientAccountOptions,
      sublimeAccountOptions: this.sublimeAccountOptions,
    });
  }

  openWithdraw(): void {
    this.$modal.show("wallet-withdraw-modal", {
      companyId: this.companyId,
      sublimeAccountOptions: this.sublimeAccountOptions,
    });
  }

  openTransfer(): void {
    this.$modal.show("wallet-transfer-modal", {
      companyId: this.companyId,
      sublimeAccountOptions: this.sublimeAccountOptions,
    });
  }

  openReverse(transactionId: number): void {
    this.$modal.show("wallet-reverse-modal", {transactionId});
  }

  showRelated(relatedId: number): void {
    const cached = this.relatedCache[relatedId];
    if (cached) {
      this.relatedTransaction = cached;
      this.$modal.show("wallet-related-transaction-modal");
      return;
    }
    Application.startLoading();
    WalletService.getTransaction(this.companyId, relatedId).then(
        res => {
          this.$set(this.relatedCache, relatedId, res.data);
          this.relatedTransaction = res.data;
          Application.stopLoading();
          this.$modal.show("wallet-related-transaction-modal");
        },
        err => processError(err, this),
    );
  }

  sublimeAccountLabel(id: number): string {
    const account = this.sublimeAccounts.find(item => item.id === id);
    if (!account) {
      return String(id);
    }
    return this.formatAccount(account.bankName, account.accountNumber);
  }

  clientAccountLabel(id: number | null): string {
    if (!id) {
      return "";
    }
    const account = this.clientAccounts.find(item => item.id === id);
    if (!account) {
      return String(id);
    }
    return this.formatAccount(account.bankName, account.accountNumber);
  }

  formatAccount(bankName: string | null, accountNumber: string | null): string {
    return [bankName, accountNumber].filter(Boolean).join(" ") || "";
  }

  formatCents(cents: number): string {
    return new Intl.NumberFormat("en-US", {style: "currency", currency: "USD"}).format(cents / 100);
  }

  formatDate(dateStr: string): string {
    if (!dateStr) {
      return "";
    }
    return moment(dateStr).format("MM-DD-YYYY");
  }
}
